import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { FloatingVideoCall } from '@/components/video/FloatingVideoCall';
import { AvailabilityVideoCall } from '@/components/video/AvailabilityVideoCall';
import { PublicCustomerVideoCall } from '@/components/video/PublicCustomerVideoCall';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Video, Phone } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { VideoCallMode } from '@/types/availability';
import { toast } from 'sonner';
import { isToday } from 'date-fns';
import { LoginForm } from '@/components/auth/LoginForm';

export function VideoCallPage() {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, dir, language } = useLanguage();
  const { currentUser, loading: authLoading } = useAuth();
  
  // Determine call mode based on URL pattern
  const [callMode, setCallMode] = useState<VideoCallMode>('appointment');
  const [coachId, setCoachId] = useState<string>('');
  const [customerLinkId, setCustomerLinkId] = useState<string>('');
  

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointmentData, setAppointmentData] = useState<any>(null);
  const [showVideoCall, setShowVideoCall] = useState(false);

  const loadAppointment = async () => {
    if (!linkId) return;
    
    try {
      setLoading(true);
      
      // Get appointment data directly using the appointment ID
      const appointmentDoc = await getDoc(doc(db, 'appointments', linkId));
      
      if (!appointmentDoc.exists()) {
        setError('Appointment not found');
        setLoading(false);
        return;
      }

      const appointmentData = appointmentDoc.data();
      const appointmentDate = appointmentData.date instanceof Date ? appointmentData.date : appointmentData.date.toDate();

      // Check if appointment is today
      if (!isToday(appointmentDate)) {
        setError('Video calls can only be used on the appointment day');
        setLoading(false);
        return;
      }

      // Get customer and coach names
      const [customerDoc, coachDoc] = await Promise.all([
        getDoc(doc(db, 'users', appointmentData.customerId)),
        getDoc(doc(db, 'users', appointmentData.coachId))
      ]);

      const customerData = customerDoc.data();
      const coachData = coachDoc.data();

      // Get language-appropriate names with full names
      const customerName = language === 'ar' 
        ? `${customerData?.firstNameAr || customerData?.firstName || customerData?.firstNameEn || 'مستفيد'} ${customerData?.lastNameAr || customerData?.lastName || ''}`.trim()
        : `${customerData?.firstNameEn || customerData?.firstName || customerData?.firstNameAr || 'Customer'} ${customerData?.lastNameEn || customerData?.lastName || ''}`.trim();
        
      const coachName = language === 'ar'
        ? `${coachData?.firstNameAr || coachData?.firstName || coachData?.firstNameEn || 'مدرب'} ${coachData?.lastNameAr || coachData?.lastName || ''}`.trim()
        : `${coachData?.firstNameEn || coachData?.firstName || coachData?.firstNameAr || 'Coach'} ${coachData?.lastNameEn || coachData?.lastName || ''}`.trim();

      setAppointmentData({
        id: linkId,
        customerId: appointmentData.customerId,
        coachId: appointmentData.coachId,
        customerName,
        coachName,
        date: appointmentDate
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading appointment:', error);
      setError('Failed to load appointment');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!linkId) {
      setError('Invalid video call link');
      setLoading(false);
      return;
    }

    // Determine call mode from URL pattern
    if (location.pathname.includes('/video-call/customer/')) {
      // Customer-specific availability link: /video-call/customer/{linkId}
      // Format: coachId-customerId-timestamp
      setCallMode('availability');
      setCustomerLinkId(linkId);
      setLoading(false);
      return;
    } else if (linkId.startsWith('coach-')) {
      // New availability-based call: /video-call/coach-{coachId}
      setCallMode('availability');
      setCoachId(linkId.replace('coach-', ''));
      setLoading(false);
      return;
    } else if (linkId.includes('-') && linkId.split('-').length >= 3) {
      // Legacy customer-specific availability link: /video-call/{linkId}
      // Format: coachId-customerId-timestamp
      setCallMode('availability');
      setCustomerLinkId(linkId);
      setLoading(false);
      return;
    } else {
      // Existing appointment-based call: /video-call/{appointmentId}
      setCallMode('appointment');
      loadAppointment();
    }
  }, [linkId, location.pathname]);

  // Route to appropriate component based on mode
  if (callMode === 'availability') {
    if (customerLinkId) {
      // Customer-specific link (no login required)
      return <PublicCustomerVideoCall linkId={customerLinkId} />;
    } else {
      // General coach availability (requires login)
      if (authLoading) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        );
      }
      
      if (!currentUser) {
        // Show login form for unauthenticated users
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Video className="h-6 w-6" />
                  {language === 'ar' ? 'تسجيل الدخول للمكالمة' : 'Login to Join Call'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <LoginForm 
                  onSuccess={() => {
                    // Login successful, the component will re-render with currentUser
                  }}
                  showSignupLink={false}
                />
              </CardContent>
            </Card>
          </div>
        );
      }
      
      return <AvailabilityVideoCall coachId={coachId} />;
    }
  }


  // Rest of the component handles appointment mode
  const handleJoinCall = () => {
    // Double-check date validation before joining
    if (appointmentData?.date && !isToday(appointmentData.date)) {
      toast.error('Video calls can only be used on the appointment day');
      return;
    }
    setShowVideoCall(true);
  };

  const handleCallEnded = () => {
    setShowVideoCall(false);
    toast.success('Video call ended');
    // Optionally redirect to a thank you page
    // navigate('/thank-you');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium">Loading video call...</p>
            <p className="text-sm text-muted-foreground">Please wait while we prepare your session</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-secondary/20 p-4">
        <Card className="w-full max-w-sm mx-auto shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-xl">
              {t('videoCall.error.title') || 'Video Call Error'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert variant="destructive" className="border-destructive/20">
              <AlertDescription className="text-center">
                {error}
              </AlertDescription>
            </Alert>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full h-12 text-base font-medium"
              size="lg"
            >
              {t('videoCall.error.goHome') || 'Go Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showVideoCall && appointmentData) {
    return (
      <div className="fixed inset-0 bg-black z-50">
        <FloatingVideoCall 
          roomId={appointmentData.id}
          userRole="customer"
          userName={appointmentData.customerName}
          otherParticipantName={
            dir === 'rtl' 
              ? `المدرب ${appointmentData.coachName}`
              : appointmentData.coachName
          }
          onCallEnded={handleCallEnded}
          open={showVideoCall}
          appointmentDate={appointmentData.date}
          forceFullscreen={true}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/20 p-4 flex items-center justify-center">
      <div className="w-full max-w-sm mx-auto">
        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-6">
            <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Video className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('videoCall.join.title')}
            </CardTitle>
            <p className="text-muted-foreground mt-2">
              {t('videoCall.join.description')}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Participant Info */}
            <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-4 rounded-xl border border-primary/10">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t('videoCall.join.coach')}
                  </span>
                  <span className="font-semibold text-primary">
                    {appointmentData.coachName}
                  </span>
                </div>
                <div className="h-px bg-primary/20"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t('videoCall.join.customer')}
                  </span>
                  <span className="font-semibold">
                    {appointmentData.customerName}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={handleJoinCall} 
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg"
                size="lg"
              >
                <Video className="h-5 w-5 ltr:mr-4 rtl:ml-4" />
                {t('videoCall.join.joinCall')}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="w-full h-12 text-base border-2 hover:bg-muted/50"
                size="lg"
              >
                {t('videoCall.join.cancel')}
              </Button>
            </div>
            
            {/* Expiry Notice */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-full text-sm border border-amber-200">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                {t('videoCall.join.expires')}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Bottom Safe Area for Mobile */}
        <div className="h-8"></div>
      </div>
    </div>
  );
}
